import { useState } from 'react'

import { useNotification } from '../../../../../hooks'
import {
  ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_ADD,
  ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_EDIT,
} from '../../../../Entity'
import {
  DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE,
  DocFlowProfileHeaderInfoItemsFields,
} from '../../../DocFlow.constants'
import { useDocFlowContext } from '../../../DocFlow.hooks'
import { isDocFlowPhaseResult } from '../../../DocFlow.utils.phases'
import { DocFlowEditFinalDocumentModal } from '../../../DocFlowEditFinalDocumentModal'
import { useDocFlowProfileHeaderConnectedRequests } from '../useDocFlowProfileHeaderConnectedRequests'
import { useDocFlowProfileHeaderEditBuildingScope } from '../useDocFlowProfileHeaderEditBuildingScope'

import {
  DOC_FLOW_PROFILE_HEADER_INFO_ITEM_CONNECTED_REQUESTS_ACTIONS_KEY,
  DOC_FLOW_PROFILE_HEADER_INFO_ITEM_UK_CREDITS_ACTIONS_KEY,
} from './useDocFlowProfileHeaderInfoItems.constants'
import {
  getDocFlowProfileHeaderInfoItemFinalNameInitialValues,
  getDocFlowProfileHeaderInfoItemsEditFinalDocTitlePayload,
  getPreparedDocFlowProfileHeaderInfoItems,
  renderDocFlowProfileHeaderInfoItem,
} from './useDocFlowProfileHeaderInfoItems.utils'

import * as styles from './useDocFlowProfileHeaderInfoItems.module.css'

export function useDocFlowProfileHeaderInfoItems({ onEditTitle }) {
  const {
    activePhase,
    asyncActionInProccess,
    docFlow,
    docFlowInitiated,
    handleCloseModal,
    handleReloadDocFlow,
    setModal,
  } = useDocFlowContext()

  const { handleAddSuccessNotification } = useNotification()

  const { handleOpenEditBuildingScopeModal } =
    useDocFlowProfileHeaderEditBuildingScope()

  const { handleOpenConnectRequestsModal, handleRemoveConnectedRequest } =
    useDocFlowProfileHeaderConnectedRequests()

  const [isRefreshing, setRefreshing] = useState(false)

  const isResultPhase = isDocFlowPhaseResult(activePhase)

  const isPageLoading = !docFlowInitiated || activePhase === null

  const infoItems = !isPageLoading
    ? getPreparedDocFlowProfileHeaderInfoItems({
        data: docFlow,
        disableEdit: asyncActionInProccess,
        isResultPhase,
        onRemoveConnectedRequest: handleRemoveConnectedRequest,
      })
    : null

  function handleClickRefreshBalance() {
    setRefreshing(true)

    handleReloadDocFlow()
      .then(() => {
        handleAddSuccessNotification('Notification.CommonTitleRefreshedKey')
      })
      .finally(() => {
        setRefreshing(false)
      })
  }

  function handleClickEditFinalDocumentTitleButton(e, value) {
    setModal(
      <DocFlowEditFinalDocumentModal
        profileAction
        fieldName={DOC_FLOW_FORM_FIELD_FINAL_DOCUMENT_TITLE}
        getPayload={getDocFlowProfileHeaderInfoItemsEditFinalDocTitlePayload}
        initialValues={getDocFlowProfileHeaderInfoItemFinalNameInitialValues({
          phase: activePhase,
          value,
        })}
        onClose={handleCloseModal}
        onSubmit={onEditTitle}
      />
    )
  }

  const buildingScopeItemHandlers = {
    [ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_ADD]: {
      onClick: handleOpenEditBuildingScopeModal,
    },
    [ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_EDIT]: {
      onClick: handleOpenEditBuildingScopeModal,
    },
  }

  const infoItemsHandlers = {
    [DocFlowProfileHeaderInfoItemsFields.BuildingGroups]:
      buildingScopeItemHandlers,
    [DocFlowProfileHeaderInfoItemsFields.Buildings]: buildingScopeItemHandlers,
    [DocFlowProfileHeaderInfoItemsFields.ConnectedRequests]: {
      [DOC_FLOW_PROFILE_HEADER_INFO_ITEM_CONNECTED_REQUESTS_ACTIONS_KEY]: {
        onClick: handleOpenConnectRequestsModal,
      },
    },
    [DocFlowProfileHeaderInfoItemsFields.FinalDocumentTitle]: {
      [ENTITY_PROFILE_HEADER_INFO_ITEM_ACTIONS_KEY_EDIT]: {
        onClick: handleClickEditFinalDocumentTitleButton,
      },
    },
    [DocFlowProfileHeaderInfoItemsFields.UkEpostCredits]: {
      [DOC_FLOW_PROFILE_HEADER_INFO_ITEM_UK_CREDITS_ACTIONS_KEY]: {
        onClick: handleClickRefreshBalance,
      },
    },
  }

  const infoItemsProps = {
    handlers: infoItemsHandlers,
    isLoading: !docFlowInitiated || activePhase === null,
    itemActionsProps: {
      buttonsProps: {
        [DOC_FLOW_PROFILE_HEADER_INFO_ITEM_UK_CREDITS_ACTIONS_KEY]: {
          className: styles.refreshButton,
          isLoading: isRefreshing,
        },
      },
    },
    items: infoItems,
    renderItem: renderDocFlowProfileHeaderInfoItem,
  }

  return { infoItemsProps }
}
